/**
 * @file billCreditRead.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 *
 * A PDF read on Add Bill that says it is a credit note (Cam, 30 Oct 2026 —
 * Mole Valley's FSCN0019792): on 27 Oct it was uploaded in Add Bill, read as
 * a credit note, its due date emptied ("a credit has no due date") — and
 * saved as a BILL, which Xero would not approve without a due date. Add Bill
 * now offers to enter it as a supplier credit instead.
 *
 *   - A NEW bill in bill mode: asked once the PDF is read — enter it as a
 *     supplier credit (the screen becomes Add Supplier Credit with what was
 *     read; "Credit is for" is still the user's to choose), keep it as a bill
 *     (not asked again for this upload), or decide when saving (it stays a
 *     bill for now; the save asks before anything is sent).
 *   - An existing bill being edited: a bill can never become a credit (the
 *     server refuses TYPE_CHANGE_REFUSED), so it is only said, as a note.
 *   - Bulk Add Bills' review: never asked — the batch item has its own kind
 *     and the server refuses a mismatch (KIND_MISMATCH).
 *   - Add Supplier Credit (credit mode): nothing changes.
 */

/** Does this read say the document is a credit note? */
export function readsAsCreditNote(read) {
  return String(read?.document_type || '').trim().toLowerCase() === 'credit_note';
}

/**
 * What Add Bill does with a read: 'ask' (a new bill in bill mode reading as a
 * credit note), 'note' (an existing bill being edited), or null (nothing —
 * not a credit note, already a credit, or Bulk Add Bills' review).
 *
 * @param {object} read — the reader's `suggested`
 * @param {{ transactionType?: string, editMode?: boolean, bulk?: boolean }} ctx
 * @returns {'ask'|'note'|null}
 */
export function creditReadAction(read, { transactionType = 'BILL', editMode = false, bulk = false } = {}) {
  if (bulk) return null;
  if (!readsAsCreditNote(read)) return null;
  if (String(transactionType || 'BILL').trim().toUpperCase() !== 'BILL') return null;
  return editMode ? 'note' : 'ask';
}

/**
 * Should the save ask first? Only a new bill in bill mode whose read said
 * credit note and was never answered (dismissed with "Decide when saving").
 *
 * @param {{ answer?: 'bill'|'credit'|null }|null} state — the current upload's credit-note read
 */
export function saveNeedsCreditAnswer(state, { transactionType = 'BILL', editMode = false, bulk = false } = {}) {
  if (!state || state.answer) return false;
  if (bulk || editMode) return false;
  return String(transactionType || 'BILL').trim().toUpperCase() === 'BILL';
}

/** The supplier a read names: the contact it matched, else the name printed on it. */
export function readSupplierName(read) {
  const m = read?.supplier_match;
  if (m?.account_id && m.company_name) return String(m.company_name).trim();
  const printed = String(m?.printed_name || read?.supplier_name || '').trim();
  return printed || '';
}

/** The answers, with what each does — the dialog's round buttons carry only an icon. */
export const CREDIT_READ_CHOICES = Object.freeze({
  credit: {
    label: 'Enter as a supplier credit',
    icon: 'assignment_return',
    hint: 'The screen becomes Add Supplier Credit with what was read. Choose what the credit is for, then save.'
  },
  bill: {
    label: 'Keep it as a bill',
    icon: 'receipt_long',
    hint: 'Nothing changes, and you are not asked again for this PDF. A bill needs a due date before Xero will approve it.'
  },
  later: {
    label: 'Decide when saving',
    icon: 'close',
    hint: 'It stays a bill for now. You are asked again when you save.'
  },
  back: {
    label: 'Go back',
    icon: 'close',
    hint: 'Nothing is saved.'
  }
});

/**
 * The question's words.
 *
 * @param {{ number?: string, supplier?: string, stage?: 'upload'|'save', relines?: boolean }} args
 *   relines — switching takes the credit's lines from the PDF again, in place
 *   of the PO's (said at save, when the lines on the form came from a PO).
 * @returns {{ title: string, message: string, choices: Array<{ key: string, label: string, icon: string, hint: string }>, cancelText: string }}
 */
export function creditReadQuestion({ number = '', supplier = '', stage = 'upload', relines = false } = {}) {
  const num = String(number || '').trim();
  const who = String(supplier || '').trim();
  const subject = `${num || 'This PDF'}${who ? ` from ${who}` : ''}`;
  const message = `${subject} reads as a credit note, not a bill. Enter it as a supplier credit? (A credit goes to Xero as a credit note and needs no due date.)`;
  const credit = { key: 'credit', ...CREDIT_READ_CHOICES.credit };
  if (relines) credit.hint = `${credit.hint} Its lines are taken from the PDF again, in place of the PO's.`;
  const cancel = stage === 'save' ? { key: 'back', ...CREDIT_READ_CHOICES.back } : { key: 'later', ...CREDIT_READ_CHOICES.later };
  return {
    title: 'This is a credit note',
    message,
    choices: [credit, { key: 'bill', ...CREDIT_READ_CHOICES.bill }, cancel],
    cancelText: cancel.label
  };
}

/** Said under the PDF once the screen has become Add Supplier Credit. */
export const CREDIT_READ_SWITCHED_NOTE = 'Read as a credit note, so it is entered as a supplier credit. Choose what the credit is for, then save.';

/** Said when a bill being edited is given a PDF that reads as a credit note. */
export const CREDIT_READ_EDIT_NOTE = "This PDF reads as a credit note — a bill can't become a credit; add it with Add Supplier Credit.";
